import React, { memo, useMemo, useCallback, Fragment, useEffect, useImperativeHandle } from 'react';
import './index.scss';
import { ProposalBlockContext, ProposalCollapseContext, useProposalContext } from '../../ProposalContext';
import { contentTree, isAlwaysVisible, navLabel } from '../SectionBar/navGroups';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { BlockBase, BlockBaseHandle } from '@modules/proposal/Blocks/BlockBase/BlockBase';
import { R } from '@core/utils/r';
import { PageBottomToolbar } from './PageBottomToolbar';
import { Proposal } from '@modules/proposal/Blocks/types';
import { ProposalBlockFormData } from '../../ProposalForm';
import { BlockBaseReadonly } from '@modules/proposal/Blocks/BlockBase/BlockBaseReadonly';
import { css } from '@emotion/react';
import classNames from 'classnames';
import { openCopyBlockTemplateModal } from './CopyBlockTemplateModal';
import { useLazyEffect } from '@core/utils/react';

export interface ProposalBlocsHandle {
  openSettings(index: number): void;
  /** Expand every collapsed ancestor of a block (and the block itself) so it can be scrolled to. */
  reveal(index: number): void;
}

// A closed nested block is hidden, background included, so its title row shows the block's own background colour and
// image (Settings tab) instead of the page's white
function closedRowBackground(block: ProposalBlockFormData): React.CSSProperties {
  const advance = block.data?.advance;
  return { backgroundColor: advance?.backgroundColor || undefined, background: advance?.background || undefined };
}

export interface ProposalBlcoksProps {
  blockRefs: React.RefObject<HTMLDivElement>[];
  moveTo(index: number): void;
  editable?: boolean;
  partiallyEditable?: boolean;
  handlesRef?: React.RefObject<ProposalBlocsHandle>;
}

export const ProposalBlocs = memo((props: ProposalBlcoksProps) => {
  const { blockRefs, moveTo, editable = true, partiallyEditable = false } = props;
  const { form } = useProposalContext()!;
  const proposal = useProposalContext();
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);
  // Nested-block toggles use the same colour as the section chevrons (Settings → carousel section colour)
  const carouselColor = proposal?.form.value.carouselSectionColor || '#000000';
  const formValue = useNonNilObservable(form.value$);
  const blocks = useMemo(() => R.sortBy(formValue.blocks, b => b.menuOrder.toString()), [formValue.blocks]);
  const blockItems = useObservable(form.controls.blocks.items$, []);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const handleRefs = useMemo(() => blockItems.map(() => React.createRef<BlockBaseHandle>()), [blockItems]);
  const [openSettingsIndex, setOpenSettingsIndex] = React.useState<number | null>(null);

  // ── Collapse tree (any depth) ──
  // Parents: explicit "Show under parent section" links + each section's following blocks. Collapsing a block
  // hides all its descendants; every nested block has its own toggle. State is keyed by block id so it survives
  // reordering. Read-only views start with sections collapsed (as before); the builder starts fully expanded.
  const tree = useMemo(() => contentTree(formValue.blocks), [formValue.blocks]);
  const [collapsedIds, setCollapsedIds] = React.useState<Set<string>>(() => new Set());
  const seenIdsRef = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    if (editable) return;
    // Pricing / signing sections start open: the client must always see them
    const fresh = formValue.blocks.filter(b => b.isSection && !isAlwaysVisible(b) && b.id && !seenIdsRef.current.has(b.id)).map(b => b.id);
    formValue.blocks.forEach(b => b.id && seenIdsRef.current.add(b.id));
    if (fresh.length) setCollapsedIds(current => new Set(Array.from(current).concat(fresh)));
  }, [editable, formValue.blocks]);

  const isCollapsed = useCallback((index: number) => collapsedIds.has(formValue.blocks[index]?.id), [collapsedIds, formValue.blocks]);
  const toggle = useCallback(
    (index: number, collapsed?: boolean) => {
      const id = formValue.blocks[index]?.id;
      if (!id) return;
      setCollapsedIds(current => {
        const next = new Set(current);
        if (collapsed ?? !next.has(id)) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [formValue.blocks],
  );
  const collapseContext = useMemo(() => ({ isCollapsed, toggle }), [isCollapsed, toggle]);
  // Hidden = some ancestor is collapsed
  const hiddenByAncestor = useCallback(
    (index: number) => {
      for (let p = tree.parentOf.get(index); p !== undefined; p = tree.parentOf.get(p)) if (isCollapsed(p)) return true;
      return false;
    },
    [tree, isCollapsed],
  );
  const reveal = useCallback(
    (index: number) => {
      const ids: string[] = [];
      for (let i: number | undefined = index; i !== undefined; i = tree.parentOf.get(i)) ids.push(formValue.blocks[i]?.id);
      setCollapsedIds(current => new Set(Array.from(current).filter(id => !ids.includes(id))));
    },
    [tree, formValue.blocks],
  );

  const listCss = useMemo(() => {
    if (primaryColor) {
      return css`
        li::marker {
          color: ${primaryColor};
        }
      `;
    } else {
      return css`
        li::marker {
          color: #45a8f7;
        }
      `;
    }
  }, [primaryColor]);

  const openSettings = useCallback(
    (blockIndex: number) => {
      if (handleRefs[blockIndex] && handleRefs[blockIndex].current) {
        handleRefs[blockIndex].current!.openSettings();
      }
    },
    [handleRefs],
  );

  useLazyEffect(() => {
    if (openSettingsIndex !== null) {
      openSettings(openSettingsIndex);
      setOpenSettingsIndex(null);
    }
  }, [openSettingsIndex, openSettings]);

  useImperativeHandle(props.handlesRef, () => ({ openSettings, reveal }));

  const onDelete = useCallback(
    (id: string) => {
      const newBlock = R.pipe(
        formValue.blocks,
        R.ops.filter(b => b.id !== id),
        R.ops.sortBy(b => b.menuOrder),
      ).map((b, i) => ({ ...b, menuOrder: i }));

      form.patchValue({ blocks: newBlock });
    },
    [form, formValue.blocks],
  );

  const addNewBlock = useCallback(
    (type: Proposal.BlockType, variation: string, at?: number, data?: ProposalBlockFormData) => {
      const menuOrder = at || blockItems.length;
      const newBlock: ProposalBlockFormData = {
        id: `widget-${type}-${variation}` + R.random(1, 9999),
        type,
        variation,
        menuOrder,
        locked: false,
        data: (data && data.data) || null,
        isSection: data ? data.isSection : false,
        title: undefined,
        description: undefined,
        image: undefined,
        titlePosition: 'left',
        navGroup: undefined,
        hideInNav: false,
        isParentSection: false,
        parentId: undefined,
        titleFormat: undefined,
      };

      let newBlocks = [...form.value.blocks].map(b => {
        return {
          ...b,
          menuOrder: b.menuOrder >= menuOrder ? b.menuOrder + 1 : b.menuOrder,
        };
      });
      newBlocks.push(newBlock);
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
      moveTo(at || newBlocks.length - 1);
      if ([Proposal.BlockType.pdf].includes(type)) setOpenSettingsIndex(at || newBlocks.length - 1);
    },
    [blockItems.length, form, moveTo],
  );

  const onAdd = useCallback(
    (type: Proposal.BlockType, variation: string, at?: number, data?: ProposalBlockFormData[]) => {
      if (type === Proposal.BlockType.blockTemplate) {
        if (!!data?.length) {
          R.reverse(data).forEach(d => {
            addNewBlock(d.type, d.variation, at, d);
          });
        }
      } else {
        addNewBlock(type, variation, at);
      }
    },
    [addNewBlock],
  );

  const onMove = useCallback(
    (id: string, direction: number) => {
      const blockIndex = formValue.blocks.findIndex(b => b.id === id);
      const blockA = formValue.blocks[blockIndex];
      const blockB = formValue.blocks[blockIndex + direction];
      const newBlockA = { ...blockA, menuOrder: blockA.menuOrder + direction };
      const newBlockB = { ...blockB, menuOrder: blockB.menuOrder - direction };
      let newBlocks = formValue.blocks.map(b => {
        if (b.id === id) return newBlockA;
        if (b.id === blockB.id) return newBlockB;
        return b;
      });
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
    },

    [form, formValue.blocks],
  );

  const onCopy = useCallback(
    (id: string) => {
      const newBlock = { ...formValue.blocks.find(b => b.id === id)! };
      newBlock.id = newBlock.id + R.random(1, 1000);
      newBlock.menuOrder += 1;

      let newBlocks = formValue.blocks.map(b => {
        return {
          ...b,
          menuOrder: b.menuOrder >= newBlock.menuOrder ? b.menuOrder + 1 : b.menuOrder,
        };
      });
      newBlocks.push(newBlock);
      newBlocks = R.sortBy(newBlocks, 'menuOrder').map((b, i) => ({ ...b, menuOrder: i }));
      form.patchValue({ blocks: newBlocks });
    },
    [form, formValue.blocks],
  );

  const onCopyBlock = useCallback(
    (id: string) => {
      const blocks = formValue.blocks;
      openCopyBlockTemplateModal({ id, blocks, blockItems: blockItems });
    },
    [blockItems, formValue.blocks],
  );

  const blocksContent = useMemo(() => {
    return blockItems.map((blockForm, index) => {
      // Workaround to avoid error when block is deleted or page is moved
      if (!blockForm) return <div key={R.random(1, 9999).toString()}></div>;
      if (!blockForm.value.type) return <div key={blockForm.value.id.toString()}></div>;
      const nested = tree.parentOf.has(index);
      const collapsed = isCollapsed(index);
      // if (blockForm.value.id !== block.id) return <div key={block.id.toString()}></div>;
      return (
        // Read-only mode renders BlockBaseReadonly which takes no ref — attach the scroll ref to
        // the wrapper instead so section navigation (preview page) can scroll to blocks.
        <div
          key={blockForm.value.id}
          ref={editable ? undefined : blockRefs[index]}
          // Kept mounted (no form/edit state lost) and just hidden while a parent is collapsed
          style={hiddenByAncestor(index) ? { display: 'none' } : undefined}
          className={classNames('proposal-tree-block', { 'is-nested': nested })}
        >
          {/* Nested (non-section) blocks get their own toggle: a small chevron while open, a title row when closed.
              Sections keep their own header chevron (BlockContainer), wired to the same state. */}
          {nested && !blockForm.value.isSection && (
            <div
              className={classNames('proposal-tree-toggle', { 'is-collapsed': collapsed })}
              style={{ color: carouselColor, ...(collapsed ? closedRowBackground(blockForm.value) : {}) }}
              onClick={() => toggle(index)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <i className={`bx bx-chevron-${collapsed ? 'right' : 'down'}`} />
              {collapsed && <span className="proposal-tree-title">{navLabel(blockForm.value)}</span>}
            </div>
          )}
          <div style={nested && !blockForm.value.isSection && collapsed ? { display: 'none' } : undefined}>
          <ProposalBlockContext.Provider value={{ blockForm, blockIndex: index }}>
            {editable && (
              <BlockBase
                ref={blockRefs[index]}
                isLast={index === blockItems.length - 1}
                isFirst={index === 0}
                onAdd={onAdd}
                onDelete={onDelete}
                onMove={onMove}
                onCopy={onCopy}
                onCopyBlock={onCopyBlock}
                partiallyEditable={partiallyEditable}
                handleRef={handleRefs[index]}
              />
            )}
            {!editable && <BlockBaseReadonly />}
          </ProposalBlockContext.Provider>
          </div>
        </div>
      );
    });
  }, [blockItems, editable, blockRefs, onAdd, onDelete, onMove, onCopy, onCopyBlock, partiallyEditable, handleRefs, tree, isCollapsed, hiddenByAncestor, toggle, carouselColor]);

  return (
    <div className="proposal-page builder" css={listCss}>
      <ProposalCollapseContext.Provider value={collapseContext}>
        <div ref={contentRef}>{blocksContent}</div>
      </ProposalCollapseContext.Provider>
      <div style={{ overflow: 'auto' }}>{editable && !partiallyEditable && <PageBottomToolbar onAdd={onAdd} size={blocks.length} />}</div>
    </div>
  );
});
